import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  ArrowRight,
  Package,
  Inbox,
  Search,
  X,
  Archive,
  Activity,
  CheckCircle2,
  LayoutGrid,
  Rows3,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { toast } from "sonner";
import TopBar from "../components/TopBar";
import StatusBadge from "../components/StatusBadge";
import SoundToggle from "../components/SoundToggle";
import QuickShare from "../components/QuickShare";
import AppFooter from "../components/AppFooter";
import {
  api,
  formatINR,
  formatDate,
  shortId,
  buildShareUrl,
} from "../lib/apiClient";
import { playChime } from "../lib/notify";

function CreateInquiryDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    internal_name: "",
    from_city: "",
    to_city: "",
    pickup_address: "",
    drop_address: "",
    distance_km: "",
    weight: "",
    weight_unit: "tons",
    material: "",
    load_notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null); // after-success view

  const reset = () =>
    setForm({
      internal_name: "",
      from_city: "",
      to_city: "",
      pickup_address: "",
      drop_address: "",
      distance_km: "",
      weight: "",
      weight_unit: "tons",
      material: "",
      load_notes: "",
    });

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) {
      // reset on close
      setCreated(null);
      reset();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.from_city || !form.to_city || !form.weight || !form.material) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        weight: parseFloat(form.weight),
        distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
        internal_name: form.internal_name.trim() || null,
        pickup_address: form.pickup_address.trim() || null,
        drop_address: form.drop_address.trim() || null,
        load_notes: form.load_notes.trim() || null,
      };
      const res = await api.post("/inquiries", payload);
      toast.success("Inquiry created");
      setCreated(res.data);
      onCreated(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create inquiry");
    } finally {
      setLoading(false);
    }
  };

  const createAnother = () => {
    reset();
    setCreated(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[720px] rounded-none border-2 border-zinc-900 max-h-[90vh] overflow-y-auto"
        data-testid="create-inquiry-dialog"
      >
        {created ? (
          <div data-testid="created-success-panel">
            <DialogHeader>
              <div className="label-overline mb-2 flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-[#16A34A]" />
                Inquiry created
              </div>
              <DialogTitle className="text-2xl tracking-tight">
                Share the link with transporters
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                This link expires on {formatDate(created.expires_at)}. Anyone
                with the link can submit a quote — no login required.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 border border-zinc-900 p-4">
              <div className="label-overline mb-1.5">Transporter Link</div>
              <div
                data-testid="created-share-url"
                className="mono text-[13px] leading-snug break-all text-zinc-900 font-semibold mb-3"
              >
                {buildShareUrl(created.share_token)}
              </div>
              <QuickShare
                token={created.share_token}
                from_city={created.from_city}
                to_city={created.to_city}
                weight={created.weight}
                weight_unit={created.weight_unit}
                material={created.material}
                size="md"
                testIdPrefix="created-share"
              />
            </div>

            {created.internal_name && (
              <div className="mt-3 text-xs text-zinc-500">
                Internal label:{" "}
                <span className="font-semibold text-zinc-700">
                  {created.internal_name}
                </span>
              </div>
            )}

            <DialogFooter className="pt-5">
              <Button
                type="button"
                variant="outline"
                data-testid="create-another-btn"
                onClick={createAnother}
                className="h-11 rounded-none border-zinc-300 hover:bg-zinc-100 hover:text-zinc-900"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Another
              </Button>
              <Button
                type="button"
                data-testid="done-btn"
                onClick={() => handleOpenChange(false)}
                className="h-11 rounded-none bg-[#0033CC] hover:bg-[#0029a8] text-white"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="label-overline mb-2">New Record</div>
              <DialogTitle className="text-2xl tracking-tight">
                Create Transport Inquiry
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                A shareable link will be generated for transporters to submit
                quotes. The link expires after 7 days.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="internal-name" className="label-overline">
              Internal Name{" "}
              <span className="normal-case tracking-normal text-zinc-400 font-normal">
                (optional · only you see this)
              </span>
            </Label>
            <Input
              id="internal-name"
              data-testid="input-internal-name"
              placeholder='e.g. "Q1 Reliance Mumbai Steel" – for your reference'
              value={form.internal_name}
              onChange={(e) =>
                setForm({ ...form, internal_name: e.target.value })
              }
              className="h-11 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="from-city" className="label-overline">
                From City
              </Label>
              <Input
                id="from-city"
                data-testid="input-from-city"
                placeholder="e.g. Mumbai"
                value={form.from_city}
                onChange={(e) =>
                  setForm({ ...form, from_city: e.target.value })
                }
                className="h-11 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="to-city" className="label-overline">
                To City
              </Label>
              <Input
                id="to-city"
                data-testid="input-to-city"
                placeholder="e.g. Delhi"
                value={form.to_city}
                onChange={(e) =>
                  setForm({ ...form, to_city: e.target.value })
                }
                className="h-11 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pickup-address" className="label-overline">
                Pickup Address
              </Label>
              <Textarea
                id="pickup-address"
                data-testid="input-pickup-address"
                placeholder="Factory, warehouse, gate number, landmark..."
                value={form.pickup_address}
                onChange={(e) =>
                  setForm({ ...form, pickup_address: e.target.value })
                }
                className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 min-h-[76px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="drop-address" className="label-overline">
                Drop Address
              </Label>
              <Textarea
                id="drop-address"
                data-testid="input-drop-address"
                placeholder="Delivery address, site, plant, unloading point..."
                value={form.drop_address}
                onChange={(e) =>
                  setForm({ ...form, drop_address: e.target.value })
                }
                className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 min-h-[76px]"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="distance-km" className="label-overline">
              KM
            </Label>
            <Input
              id="distance-km"
              data-testid="input-distance-km"
              type="number"
              step="0.01"
              min="0"
              placeholder="Approx distance in km"
              value={form.distance_km}
              onChange={(e) =>
                setForm({ ...form, distance_km: e.target.value })
              }
              className="h-11 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="weight" className="label-overline">
                Weight
              </Label>
              <Input
                id="weight"
                data-testid="input-weight"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: e.target.value })}
                className="h-11 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Unit</Label>
              <Select
                value={form.weight_unit}
                onValueChange={(v) => setForm({ ...form, weight_unit: v })}
              >
                <SelectTrigger
                  data-testid="select-weight-unit"
                  className="h-11 rounded-none border-zinc-300"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tons">Tons</SelectItem>
                  <SelectItem value="kg">Kg</SelectItem>
                  <SelectItem value="quintals">Quintals</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="material" className="label-overline">
              Material
            </Label>
            <Input
              id="material"
              data-testid="input-material"
              placeholder="e.g. Steel coils, Cotton bales, Cement"
              value={form.material}
              onChange={(e) => setForm({ ...form, material: e.target.value })}
              className="h-11 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="load-notes" className="label-overline">
              Notes
            </Label>
            <Textarea
              id="load-notes"
              data-testid="input-load-notes"
              placeholder="Loading instructions, vehicle restrictions, timing, contact notes..."
              value={form.load_notes}
              onChange={(e) =>
                setForm({ ...form, load_notes: e.target.value })
              }
              className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 min-h-[84px]"
            />
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              data-testid="cancel-inquiry-btn"
              className="h-11 rounded-none border-zinc-300 hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-inquiry-btn"
              disabled={loading}
              className="h-11 rounded-none bg-[#0033CC] hover:bg-[#0029a8] text-white"
            >
              {loading ? "Creating…" : "Create Inquiry"}
            </Button>
          </DialogFooter>
        </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KpiStat({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="border border-zinc-200 p-4 md:p-5 bg-white"
    >
      <div className="label-overline">{label}</div>
      <div className="text-3xl md:text-4xl font-bold tracking-tight mt-2 data-cell text-zinc-900">
        {value}
      </div>
    </div>
  );
}

function InquiryRow({ inq }) {
  return (
    <tr
      data-testid={`inquiry-row-${inq.id}`}
      className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors"
    >
      <td className="p-4">
        <Link
          to={`/inquiry/${inq.id}`}
          data-testid={`name-open-inquiry-${inq.id}`}
          className="font-semibold text-zinc-900 leading-tight hover:text-[#0033CC] underline-offset-4 hover:underline inline-flex items-center gap-1.5"
        >
          {inq.internal_name || (
            <span className="text-zinc-400 italic font-normal">
              (no internal name)
            </span>
          )}
          <ArrowRight className="h-3.5 w-3.5 text-[#0033CC]" />
        </Link>
        <div className="data-cell text-[11px] text-zinc-400 mt-0.5">
          #{shortId(inq.id)}
        </div>
      </td>
      <td className="p-4">
        <div className="flex items-center gap-2 font-medium text-zinc-900">
          <span>{inq.from_city}</span>
          <ArrowRight className="h-3.5 w-3.5 text-zinc-400" />
          <span>{inq.to_city}</span>
        </div>
        {inq.distance_km !== null && inq.distance_km !== undefined && (
          <div className="data-cell text-[11px] text-zinc-500 mt-1">
            {inq.distance_km} km
          </div>
        )}
      </td>
      <td className="p-4 data-cell">
        {inq.weight} {inq.weight_unit}
      </td>
      <td className="p-4 text-zinc-700">
        <div className="flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5 text-zinc-400" />
          {inq.material}
        </div>
      </td>
      <td className="p-4">
        <StatusBadge status={inq.status} />
      </td>
      <td className="p-4 data-cell text-right font-semibold">
        {inq.total_quotes}
      </td>
      <td className="p-4 data-cell text-right font-semibold">
        {inq.lowest_quote ? (
          <span className="text-[#16A34A]">{formatINR(inq.lowest_quote)}</span>
        ) : (
          <span className="text-zinc-400">—</span>
        )}
      </td>
      <td className="p-4 text-zinc-500 text-xs">
        {formatDate(inq.created_at)}
      </td>
      <td className="p-4 text-right">
        <div className="flex items-center justify-end gap-2">
          <QuickShare
            token={inq.share_token}
            from_city={inq.from_city}
            to_city={inq.to_city}
            weight={inq.weight}
            weight_unit={inq.weight_unit}
            material={inq.material}
            size="sm"
            testIdPrefix={`row-share-${inq.id}`}
          />
          <Link
            to={`/inquiry/${inq.id}`}
            data-testid={`open-inquiry-${inq.id}`}
            className="inline-flex items-center gap-1.5 text-[#0033CC] hover:text-[#0029a8] font-semibold text-xs tracking-wide uppercase px-2 h-8"
          >
            Open
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </td>
    </tr>
  );
}

function InquiryCard({ inq, expanded, onToggle }) {
  const accent =
    inq.status === "open"
      ? "border-l-[#0033CC]"
      : inq.status === "closed"
      ? "border-l-[#DC2626]"
      : "border-l-zinc-400";

  return (
    <div
      data-testid={`inquiry-card-${inq.id}`}
      className={`bg-white border border-zinc-200 border-l-4 ${accent} card-lift`}
    >
      <button
        type="button"
        data-testid={`card-toggle-${inq.id}`}
        onClick={onToggle}
        className="w-full text-left p-4 md:p-5 flex items-start justify-between gap-3"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <StatusBadge status={inq.status} />
            <span className="data-cell text-[10px] text-zinc-400">
              #{shortId(inq.id)}
            </span>
          </div>
          <div className="font-semibold text-zinc-900 truncate">
            {inq.internal_name || (
              <span className="text-zinc-400 italic font-normal">
                (no internal name)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-700 mt-1">
            <span className="font-medium">{inq.from_city}</span>
            <ArrowRight className="h-3.5 w-3.5 text-zinc-400" />
            <span className="font-medium">{inq.to_city}</span>
          </div>
          {inq.distance_km !== null && inq.distance_km !== undefined && (
            <div className="data-cell text-[11px] text-zinc-500 mt-1">
              {inq.distance_km} km
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="data-cell text-2xl font-bold text-zinc-900">
            {inq.total_quotes}
          </div>
          <div className="label-overline">quotes</div>
          {inq.lowest_quote ? (
            <div className="data-cell text-sm font-semibold text-[#16A34A] mt-1 whitespace-nowrap">
              {formatINR(inq.lowest_quote)}
            </div>
          ) : (
            <div className="text-xs text-zinc-400 mt-1">—</div>
          )}
        </div>
      </button>

      <div className="px-4 md:px-5 pb-3 flex items-center justify-between gap-2 text-xs text-zinc-500 border-t border-zinc-100 pt-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="data-cell">
            {inq.weight} {inq.weight_unit}
          </span>
          <span className="inline-flex items-center gap-1">
            <Package className="h-3 w-3 text-zinc-400" />
            {inq.material}
          </span>
          <span>{formatDate(inq.created_at)}</span>
        </div>
        <button
          type="button"
          onClick={onToggle}
          data-testid={`card-expand-${inq.id}`}
          className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-600 hover:text-zinc-900"
        >
          {expanded ? (
            <>
              Collapse <ChevronUp className="h-3.5 w-3.5" />
            </>
          ) : (
            <>
              Expand <ChevronDown className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      </div>

      {expanded && (
        <div
          data-testid={`card-details-${inq.id}`}
          className="px-4 md:px-5 pb-5 pt-2 border-t border-zinc-100 bg-zinc-50/40"
        >
          <div className="label-overline mb-2 mt-1">Share Transporter Link</div>
          <div className="mono text-[12px] break-all text-zinc-800 mb-3 bg-white border border-zinc-200 p-2">
            {buildShareUrl(inq.share_token)}
          </div>
          <QuickShare
            token={inq.share_token}
            from_city={inq.from_city}
            to_city={inq.to_city}
            weight={inq.weight}
            weight_unit={inq.weight_unit}
            material={inq.material}
            size="md"
            testIdPrefix={`card-share-${inq.id}`}
          />

          <div className="mt-4 flex items-center justify-between">
            <div className="label-overline">
              Expires {formatDate(inq.expires_at)}
            </div>
            <Link
              to={`/inquiry/${inq.id}`}
              data-testid={`card-open-${inq.id}`}
              className="inline-flex items-center gap-1.5 text-[#0033CC] hover:text-[#0029a8] font-semibold text-xs tracking-wide uppercase"
            >
              Open full detail
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function InquiryCardGrid({ items, expandedId, setExpandedId, emptyLabel, emptySub, onCreate }) {
  if (items.length === 0) {
    return (
      <div
        data-testid="empty-state"
        className="border border-dashed border-zinc-300 p-12 text-center bg-zinc-50/50"
      >
        <Inbox className="h-10 w-10 text-zinc-400 mx-auto mb-4" />
        <div className="font-semibold text-zinc-900 mb-1">{emptyLabel}</div>
        <p className="text-sm text-zinc-500 mb-6">{emptySub}</p>
        {onCreate && (
          <Button
            onClick={onCreate}
            data-testid="empty-create-btn"
            className="h-10 rounded-none bg-[#0033CC] hover:bg-[#0029a8] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Inquiry
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((inq) => (
        <InquiryCard
          key={inq.id}
          inq={inq}
          expanded={expandedId === inq.id}
          onToggle={() =>
            setExpandedId((cur) => (cur === inq.id ? null : inq.id))
          }
        />
      ))}
    </div>
  );
}

function InquiryTable({ items, emptyLabel, emptySub, onCreate }) {
  if (items.length === 0) {
    return (
      <div
        data-testid="empty-state"
        className="border border-dashed border-zinc-300 p-12 text-center bg-zinc-50/50"
      >
        <Inbox className="h-10 w-10 text-zinc-400 mx-auto mb-4" />
        <div className="font-semibold text-zinc-900 mb-1">{emptyLabel}</div>
        <p className="text-sm text-zinc-500 mb-6">{emptySub}</p>
        {onCreate && (
          <Button
            onClick={onCreate}
            data-testid="empty-create-btn"
            className="h-10 rounded-none bg-[#0033CC] hover:bg-[#0029a8] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Inquiry
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 overflow-x-auto bg-white">
      <table className="w-full text-sm min-w-[1280px]">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-left">
            <th className="label-overline p-4">Name / ID</th>
            <th className="label-overline p-4">Route</th>
            <th className="label-overline p-4">Weight</th>
            <th className="label-overline p-4">Material</th>
            <th className="label-overline p-4">Status</th>
            <th className="label-overline p-4 text-right">Quotes</th>
            <th className="label-overline p-4 text-right">Lowest</th>
            <th className="label-overline p-4">Created</th>
            <th className="label-overline p-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((inq) => (
            <InquiryRow key={inq.id} inq={inq} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FilterBar({ query, setQuery, statusFilter, setStatusFilter, options }) {
  return (
    <div className="flex flex-col md:flex-row gap-3 mb-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
        <Input
          data-testid="search-input"
          placeholder="Search by internal name, city, material or ID…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-11 pl-9 pr-9 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900"
        />
        {query && (
          <button
            data-testid="clear-search-btn"
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-zinc-900"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger
          data-testid="status-filter"
          className="h-11 w-full md:w-48 rounded-none border-zinc-300"
        >
          <SelectValue placeholder="Filter by status" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function Dashboard() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tab, setTab] = useState("active");
  const [query, setQuery] = useState("");
  const [activeStatus, setActiveStatus] = useState("all");
  const [archiveStatus, setArchiveStatus] = useState("all");
  const [viewMode, setViewMode] = useState("table"); // "table" | "cards"
  const [expandedId, setExpandedId] = useState(null);
  const quotesSnapshot = useRef(new Map()); // id -> total_quotes

  const notifyIfNew = (incoming) => {
    const snap = quotesSnapshot.current;
    let newTotal = 0;
    const growers = [];
    for (const inq of incoming) {
      const prev = snap.get(inq.id);
      if (prev !== undefined && inq.total_quotes > prev) {
        const delta = inq.total_quotes - prev;
        newTotal += delta;
        growers.push({ inq, delta });
      }
    }
    if (newTotal > 0) {
      playChime();
      growers.slice(0, 3).forEach(({ inq, delta }) => {
        toast.success(
          `${delta} new quote${delta > 1 ? "s" : ""} · ${
            inq.internal_name || `${inq.from_city} → ${inq.to_city}`
          }`
        );
      });
    }
    // Always refresh snapshot
    const next = new Map();
    incoming.forEach((i) => next.set(i.id, i.total_quotes));
    quotesSnapshot.current = next;
  };

  const load = async (options = {}) => {
    try {
      const res = await api.get("/inquiries");
      if (options.silent) {
        notifyIfNew(res.data);
      } else {
        // Seed snapshot without notifying on first load
        const next = new Map();
        res.data.forEach((i) => next.set(i.id, i.total_quotes));
        quotesSnapshot.current = next;
      }
      setInquiries(res.data);
    } catch {
      if (!options.silent) toast.error("Failed to load inquiries");
    } finally {
      if (!options.silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for new quotes every 10s.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        load({ silent: true });
      }
    }, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = {
    total: inquiries.length,
    open: inquiries.filter((i) => i.status === "open").length,
    closed: inquiries.filter((i) => i.status === "closed").length,
    quotes: inquiries.reduce((sum, i) => sum + (i.total_quotes || 0), 0),
  };

  const activeItems = inquiries.filter((i) => i.status === "open");
  const archiveItems = inquiries.filter(
    (i) => i.status === "closed" || i.status === "expired"
  );

  const matchesQuery = (inq) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [
      inq.internal_name,
      inq.from_city,
      inq.to_city,
      inq.pickup_address,
      inq.drop_address,
      inq.material,
      inq.load_notes,
      inq.admin_note,
      inq.id,
      shortId(inq.id),
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  };

  const applyStatus = (items, statusVal) =>
    statusVal === "all" ? items : items.filter((i) => i.status === statusVal);

  const filteredActive = applyStatus(activeItems, activeStatus).filter(
    matchesQuery
  );
  const filteredArchive = applyStatus(archiveItems, archiveStatus).filter(
    matchesQuery
  );

  return (
    <div className="min-h-screen bg-white">
      <TopBar
        right={
          <>
            <SoundToggle />
            <Button
              data-testid="new-inquiry-btn"
              onClick={() => setDialogOpen(true)}
              className="h-10 rounded-none bg-[#0033CC] hover:bg-[#0029a8] text-white px-4 gap-2"
            >
              <Plus className="h-4 w-4" />
              New Inquiry
            </Button>
          </>
        }
      />

      <CreateInquiryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={() => load()}
      />

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="mb-10">
          <div className="label-overline mb-3">Control Room</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-zinc-900">
            Inquiries
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl leading-relaxed">
            Create inquiry links, share with transporters, and compare all
            quotes in one dashboard. No login required for transporters.
          </p>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <KpiStat
            label="Total Inquiries"
            value={stats.total}
            testId="kpi-total"
          />
          <KpiStat label="Open" value={stats.open} testId="kpi-open" />
          <KpiStat label="Closed" value={stats.closed} testId="kpi-closed" />
          <KpiStat
            label="Total Quotes"
            value={stats.quotes}
            testId="kpi-quotes"
          />
        </div>

        {/* Inquiry List with Tabs */}
        {loading ? (
          <div className="border border-zinc-200 p-12 text-center text-zinc-500">
            Loading…
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <TabsList
                data-testid="tabs-list"
                className="h-auto p-1 bg-zinc-100 rounded-none border border-zinc-200 inline-flex"
              >
                <TabsTrigger
                  value="active"
                  data-testid="tab-active"
                  className="rounded-none data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-none px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold"
                >
                  <Activity className="h-3.5 w-3.5 mr-2" />
                  Active
                  <span className="ml-2 data-cell text-zinc-500">
                    {activeItems.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="archive"
                  data-testid="tab-archive"
                  className="rounded-none data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-none px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold"
                >
                  <Archive className="h-3.5 w-3.5 mr-2" />
                  Archive
                  <span className="ml-2 data-cell text-zinc-500">
                    {archiveItems.length}
                  </span>
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-3">
                <div
                  data-testid="view-toggle"
                  className="inline-flex border border-zinc-200 bg-zinc-50"
                >
                  <button
                    type="button"
                    data-testid="view-table-btn"
                    onClick={() => setViewMode("table")}
                    className={`h-9 px-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold transition-colors ${
                      viewMode === "table"
                        ? "bg-white text-zinc-900"
                        : "text-zinc-500 hover:text-zinc-900"
                    }`}
                    title="Table view"
                  >
                    <Rows3 className="h-3.5 w-3.5" />
                    Table
                  </button>
                  <button
                    type="button"
                    data-testid="view-cards-btn"
                    onClick={() => setViewMode("cards")}
                    className={`h-9 px-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold transition-colors ${
                      viewMode === "cards"
                        ? "bg-white text-zinc-900"
                        : "text-zinc-500 hover:text-zinc-900"
                    }`}
                    title="Card view"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    Cards
                  </button>
                </div>
                <div className="label-overline">
                  {tab === "active"
                    ? `${filteredActive.length} result${filteredActive.length === 1 ? "" : "s"}`
                    : `${filteredArchive.length} result${filteredArchive.length === 1 ? "" : "s"}`}
                </div>
              </div>
            </div>

            <TabsContent value="active" className="mt-0">
              <FilterBar
                query={query}
                setQuery={setQuery}
                statusFilter={activeStatus}
                setStatusFilter={setActiveStatus}
                options={[
                  { value: "all", label: "All active" },
                  { value: "open", label: "Open only" },
                ]}
              />
              {viewMode === "table" ? (
                <InquiryTable
                  items={filteredActive}
                  emptyLabel={
                    query || activeStatus !== "all"
                      ? "No inquiries match your filters"
                      : "No active inquiries"
                  }
                  emptySub={
                    query || activeStatus !== "all"
                      ? "Try clearing search or change the status filter."
                      : "Create your first transport inquiry and share the link with transporters."
                  }
                  onCreate={
                    !query && activeStatus === "all"
                      ? () => setDialogOpen(true)
                      : null
                  }
                />
              ) : (
                <InquiryCardGrid
                  items={filteredActive}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  emptyLabel={
                    query || activeStatus !== "all"
                      ? "No inquiries match your filters"
                      : "No active inquiries"
                  }
                  emptySub={
                    query || activeStatus !== "all"
                      ? "Try clearing search or change the status filter."
                      : "Create your first transport inquiry and share the link with transporters."
                  }
                  onCreate={
                    !query && activeStatus === "all"
                      ? () => setDialogOpen(true)
                      : null
                  }
                />
              )}
            </TabsContent>

            <TabsContent value="archive" className="mt-0">
              <FilterBar
                query={query}
                setQuery={setQuery}
                statusFilter={archiveStatus}
                setStatusFilter={setArchiveStatus}
                options={[
                  { value: "all", label: "Closed + Expired" },
                  { value: "closed", label: "Closed only" },
                  { value: "expired", label: "Expired only" },
                ]}
              />
              {viewMode === "table" ? (
                <InquiryTable
                  items={filteredArchive}
                  emptyLabel={
                    query || archiveStatus !== "all"
                      ? "No inquiries match your filters"
                      : "No archived inquiries"
                  }
                  emptySub={
                    query || archiveStatus !== "all"
                      ? "Try clearing search or change the status filter."
                      : "Closed and expired inquiries will appear here."
                  }
                />
              ) : (
                <InquiryCardGrid
                  items={filteredArchive}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  emptyLabel={
                    query || archiveStatus !== "all"
                      ? "No inquiries match your filters"
                      : "No archived inquiries"
                  }
                  emptySub={
                    query || archiveStatus !== "all"
                      ? "Try clearing search or change the status filter."
                      : "Closed and expired inquiries will appear here."
                  }
                />
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>

      <AppFooter className="mt-16" />
    </div>
  );
}
